import { useState } from "react";
import { CaretDown, LockSimple } from "@phosphor-icons/react";
import { PERM_GROUPS, PATRON_ONLY, presetPerms, matchesPreset, ALL_PERM_KEYS } from "../lib/permissions";

const ROLE_HINT = {
  vendeur: "Vendeur : encaisse et consulte. Il ne voit ni prix d'achat, ni marge, ni bénéfice.",
  gestionnaire: "Gestionnaire : vente, stock complet et dépenses. Le bénéfice et la trésorerie restent masqués tant que vous ne les activez pas.",
  custom: "Personnalisé : vous choisissez chaque accès ci-dessous.",
};

function Toggle({ on, onClick, label, hint, sensitive, testId, disabled }) {
  return (
    <button type="button" data-testid={testId} disabled={disabled} onClick={onClick}
      className={`w-full flex items-start gap-3 px-2.5 py-2.5 rounded-xl text-left transition-colors ${disabled ? "opacity-50" : "hover:bg-[#F6F4EF]"}`}>
      <span className={`w-11 h-6 rounded-full shrink-0 relative transition-colors mt-0.5 ${on ? "bg-[#2E7D5B]" : "bg-[#D6D0C4]"}`}>
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
      </span>
      <span className="min-w-0">
        <span className="text-sm block">
          {label}
          {sensitive && <span className="ml-1.5 text-[10px] uppercase tracking-wide bg-[#F7EFD9] text-[#9a7620] px-1.5 py-0.5 rounded-full align-middle">sensible</span>}
        </span>
        {hint && <span className="text-[11px] text-gray-500 block">{hint}</span>}
      </span>
    </button>
  );
}

function Group({ group, perms, toggle, disabled, idSuffix, extra }) {
  const [open, setOpen] = useState(group.key === "dashboard");
  const count = group.perms.filter((p) => perms?.[p.key]).length;
  return (
    <div className="border border-[#E9E4DA] rounded-2xl overflow-hidden bg-white">
      <button type="button" data-testid={`perm-group-${group.key}${idSuffix}`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3.5 py-3 bg-[#F6F4EF]/70">
        <span className="font-display font-bold text-sm flex-1 text-left">{group.title}</span>
        <span className="text-xs text-gray-500 tabular-nums">{count}/{group.perms.length}</span>
        <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="p-1.5 space-y-0.5">
          {group.perms.map((p) => (
            <Toggle key={p.key} testId={`perm-${p.key}${idSuffix}`} disabled={disabled}
              on={!!perms?.[p.key]} label={p.label} hint={p.hint} sensitive={p.sensitive}
              onClick={() => toggle(p.key)} />
          ))}
          {group.key === "sales" && extra}
        </div>
      )}
    </div>
  );
}

/** Interrupteurs groupés par thème + encadré « réservé au patron ». */
export function PermissionEditor({ perms, setPerms, role, setRole, disabled, idSuffix = "", discountField }) {
  const toggle = (key) => {
    const next = { ...perms, [key]: !perms?.[key] };
    setPerms(next);
    if (setRole && !matchesPreset(next, "vendeur") && !matchesPreset(next, "gestionnaire")) setRole("custom");
  };

  const pickRole = (r) => {
    setRole(r);
    if (r !== "custom") setPerms(presetPerms(r));
  };

  return (
    <div className="space-y-3">
      {setRole && (
        <div>
          <p className="text-sm font-semibold mb-1.5">Rôle</p>
          <div className="flex flex-wrap gap-1.5">
            {["vendeur", "gestionnaire", "custom"].map((r) => (
              <button key={r} type="button" data-testid={`role-${r}${idSuffix}`} onClick={() => pickRole(r)}
                className={`text-xs px-3.5 py-2 rounded-full capitalize min-h-[36px] ${role === r ? "bg-[#16233D] text-white" : "bg-white border border-[#E0DACE]"}`}>
                {r === "custom" ? "Personnalisé" : r}
              </button>
            ))}
          </div>
          <p data-testid={`role-hint${idSuffix}`} className="text-[11px] text-gray-500 mt-1.5">{ROLE_HINT[role] || ""}</p>
        </div>
      )}
      {PERM_GROUPS.map((g) => (
        <Group key={g.key} group={g} perms={perms} toggle={toggle} disabled={disabled}
          idSuffix={idSuffix} extra={g.key === "sales" ? discountField : null} />
      ))}
      <div className="border border-[#E9E4DA] rounded-2xl p-3.5 bg-[#F6F4EF]/50">
        <p className="font-display font-bold text-sm flex items-center gap-1.5 mb-1.5">
          <LockSimple size={14} weight="fill" /> Réservé au patron (non délégable)
        </p>
        <ul className="text-[11px] text-gray-500 space-y-0.5 list-disc pl-4">
          {PATRON_ONLY.map((x) => <li key={x}>{x}</li>)}
        </ul>
      </div>
    </div>
  );
}

export { ALL_PERM_KEYS };
